(() => {
  "use strict";

  const CONTROLLER_KEY = "NBRHeroController";
  const CONTROLLER_VERSION = "5.0.0";
  const HERO_SELECTOR = "[data-nbr-hero]";
  const INSTANCE_KEY = "__nbrHeroInstance";

  const existingController = window[CONTROLLER_KEY];

  if (
    existingController &&
    existingController.version === CONTROLLER_VERSION
  ) {
    existingController.refresh(document);
    return;
  }

  if (existingController) {
    if (
      typeof existingController.destroyAll ===
      "function"
    ) {
      existingController.destroyAll();
    } else if (
      typeof existingController.destroy ===
      "function"
    ) {
      existingController.destroy(document);
    }
  }

  const root = document.documentElement;
  const globalAbortController =
    new AbortController();

  const instances = new Set();

  let scrollFrameId = null;

  const mediaQueries = {
    reducedMotion: window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ),
    coarsePointer: window.matchMedia(
      "(pointer: coarse)"
    ),
    noHover: window.matchMedia(
      "(hover: none)"
    ),
    mobile: window.matchMedia(
      "(max-width: 749px)"
    )
  };

  function clamp(
    value,
    minimum,
    maximum
  ) {
    return Math.min(
      Math.max(value, minimum),
      maximum
    );
  }

  function lerp(
    start,
    end,
    amount
  ) {
    return (
      start +
      (end - start) *
      amount
    );
  }

  function round(
    value,
    precision = 3
  ) {
    const multiplier =
      10 ** precision;

    return (
      Math.round(
        value *
        multiplier
      ) /
      multiplier
    );
  }

  function listen(
    target,
    eventName,
    handler,
    options,
    signal
  ) {
    if (
      !target ||
      typeof target.addEventListener !==
        "function"
    ) {
      return;
    }

    target.addEventListener(
      eventName,
      handler,
      options
    );

    if (!signal) {
      return;
    }

    signal.addEventListener(
      "abort",
      () => {
        target.removeEventListener(
          eventName,
          handler,
          options
        );
      },
      { once: true }
    );
  }

  function listenToMediaQuery(
    mediaQuery,
    callback,
    signal
  ) {
    if (
      typeof mediaQuery.addEventListener ===
      "function"
    ) {
      listen(
        mediaQuery,
        "change",
        callback,
        undefined,
        signal
      );

      return;
    }

    if (
      typeof mediaQuery.addListener ===
      "function"
    ) {
      mediaQuery.addListener(callback);

      signal.addEventListener(
        "abort",
        () => {
          mediaQuery.removeListener(
            callback
          );
        },
        { once: true }
      );
    }
  }

  function getQualityMode() {
    const quality =
      root.dataset.nbrQuality;

    if (
      quality === "ultra" ||
      quality === "balanced" ||
      quality === "performance"
    ) {
      return quality;
    }

    return "balanced";
  }

  function getSmoothingAmount() {
    const quality =
      getQualityMode();

    if (quality === "ultra") {
      return 0.11;
    }

    if (quality === "balanced") {
      return 0.16;
    }

    return 1;
  }

  function getHeroes(
    container = document
  ) {
    const heroes = [];

    if (
      container &&
      container.nodeType === 1 &&
      typeof container.matches ===
        "function" &&
      container.matches(HERO_SELECTOR)
    ) {
      heroes.push(container);
    }

    if (
      container &&
      typeof container.querySelectorAll ===
        "function"
    ) {
      heroes.push(
        ...container.querySelectorAll(
          HERO_SELECTOR
        )
      );
    }

    return Array.from(
      new Set(heroes)
    );
  }

  function requestGlobalScrollUpdate() {
    if (scrollFrameId !== null) {
      return;
    }

    scrollFrameId =
      window.requestAnimationFrame(
        () => {
          scrollFrameId = null;

          instances.forEach(
            (instance) => {
              instance.updateScrollState();
            }
          );
        }
      );
  }

  function createHeroInstance(hero) {
    if (!hero) {
      return null;
    }

    if (hero[INSTANCE_KEY]) {
      return hero[INSTANCE_KEY];
    }

    const localAbortController =
      new AbortController();

    const localSignal =
      localAbortController.signal;

    const videoWrapper =
      hero.querySelector(
        "[data-nbr-video-wrapper]"
      );

    const video =
      videoWrapper
        ? videoWrapper.querySelector(
            "video"
          )
        : null;

    const state = {
      destroyed: false,
      inView: true,
      pageVisible: !document.hidden,
      readyFrameOne: null,
      readyFrameTwo: null,
      pointerFrameId: null,
      targetX: 0,
      targetY: 0,
      currentX: 0,
      currentY: 0
    };

    let intersectionObserver = null;
    let resizeObserver = null;

    function isReducedMotion() {
      return (
        mediaQueries
          .reducedMotion
          .matches
      );
    }

    function isCoarsePointer() {
      return (
        mediaQueries
          .coarsePointer
          .matches ||
        mediaQueries
          .noHover
          .matches
      );
    }

    function pointerLightingAllowed() {
      return (
        !state.destroyed &&
        state.inView &&
        state.pageVisible &&
        !isReducedMotion() &&
        !isCoarsePointer() &&
        getQualityMode() !==
          "performance"
      );
    }

    function setLighting(
      normalizedX,
      normalizedY
    ) {
      const lightX = clamp(
        50 +
          normalizedX *
            34,
        14,
        86
      );

      const lightY = clamp(
        50 +
          normalizedY *
            30,
        18,
        82
      );

      hero.style.setProperty(
        "--hero-light-x",
        `${round(lightX, 2)}%`
      );

      hero.style.setProperty(
        "--hero-light-y",
        `${round(lightY, 2)}%`
      );

      hero.style.setProperty(
        "--hero-pointer-x",
        round(normalizedX, 4)
      );

      hero.style.setProperty(
        "--hero-pointer-y",
        round(normalizedY, 4)
      );
    }

    function renderPointerLighting() {
      state.pointerFrameId = null;

      if (
        !pointerLightingAllowed()
      ) {
        return;
      }

      const smoothing =
        getSmoothingAmount();

      state.currentX = lerp(
        state.currentX,
        state.targetX,
        smoothing
      );

      state.currentY = lerp(
        state.currentY,
        state.targetY,
        smoothing
      );

      setLighting(
        state.currentX,
        state.currentY
      );

      const distance =
        Math.abs(
          state.targetX -
            state.currentX
        ) +
        Math.abs(
          state.targetY -
            state.currentY
        );

      if (distance > 0.003) {
        requestPointerRender();
      }
    }

    function requestPointerRender() {
      if (
        state.pointerFrameId !== null ||
        !pointerLightingAllowed()
      ) {
        return;
      }

      state.pointerFrameId =
        window.requestAnimationFrame(
          renderPointerLighting
        );
    }

    function resetPointerLighting(
      immediate = false
    ) {
      state.targetX = 0;
      state.targetY = 0;

      if (immediate) {
        state.currentX = 0;
        state.currentY = 0;

        setLighting(0, 0);
        return;
      }

      requestPointerRender();
    }

    function handlePointerMove(event) {
      if (
        !pointerLightingAllowed()
      ) {
        return;
      }

      const bounds =
        hero.getBoundingClientRect();

      if (
        bounds.width <= 0 ||
        bounds.height <= 0
      ) {
        return;
      }

      const relativeX =
        (
          event.clientX -
          bounds.left
        ) /
        bounds.width;

      const relativeY =
        (
          event.clientY -
          bounds.top
        ) /
        bounds.height;

      state.targetX = clamp(
        (
          relativeX -
          0.5
        ) *
          2,
        -1,
        1
      );

      state.targetY = clamp(
        (
          relativeY -
          0.5
        ) *
          2,
        -1,
        1
      );

      requestPointerRender();
    }

    function updateScrollState() {
      if (
        state.destroyed ||
        !state.inView
      ) {
        return;
      }

      const bounds =
        hero.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight ||
        document.documentElement
          .clientHeight ||
        1;

      const totalTravel =
        bounds.height +
        viewportHeight;

      const travelled =
        viewportHeight -
        bounds.top;

      const viewportProgress =
        totalTravel > 0
          ? clamp(
              travelled /
                totalTravel,
              0,
              1
            )
          : 0;

      const exitProgress =
        bounds.height > 0
          ? clamp(
              -bounds.top /
                bounds.height,
              0,
              1
            )
          : 0;

      hero.style.setProperty(
        "--hero-viewport-progress",
        viewportProgress.toFixed(4)
      );

      hero.style.setProperty(
        "--hero-scroll-progress",
        exitProgress.toFixed(4)
      );

      hero.style.setProperty(
        "--hero-scroll-offset",
        `${round(
          Math.max(
            -bounds.top,
            0
          ),
          2
        )}px`
      );
    }

    function mobileVideoDisabled() {
      if (!videoWrapper) {
        return false;
      }

      return (
        mediaQueries.mobile.matches &&
        videoWrapper.classList.contains(
          "nbr-hero__video-wrapper--desktop-only"
        )
      );
    }

    function videoShouldPlay() {
      return (
        Boolean(video) &&
        !state.destroyed &&
        state.inView &&
        state.pageVisible &&
        !isReducedMotion() &&
        !mobileVideoDisabled()
      );
    }

    function updateVideoState() {
      if (!video) {
        return;
      }

      video.muted = true;
      video.playsInline = true;

      if (videoShouldPlay()) {
        const playPromise =
          video.play();

        if (
          playPromise &&
          typeof playPromise.catch ===
            "function"
        ) {
          playPromise.catch(
            () => {
              hero.classList.add(
                "nbr-hero--video-paused"
              );
            }
          );
        }

        return;
      }

      video.pause();

      hero.classList.add(
        "nbr-hero--video-paused"
      );
    }

    function handleVideoPlaying() {
      hero.classList.add(
        "nbr-hero--video-ready"
      );

      hero.classList.remove(
        "nbr-hero--video-paused"
      );
    }

    function handleVideoPause() {
      hero.classList.add(
        "nbr-hero--video-paused"
      );
    }

    function revealHero() {
      if (state.destroyed) {
        return;
      }

      state.readyFrameOne =
        window.requestAnimationFrame(
          () => {
            state.readyFrameOne = null;

            if (state.destroyed) {
              return;
            }

            state.readyFrameTwo =
              window.requestAnimationFrame(
                () => {
                  state.readyFrameTwo =
                    null;

                  if (
                    state.destroyed
                  ) {
                    return;
                  }

                  hero.classList.add(
                    "nbr-hero--ready"
                  );

                  hero.dataset.nbrHeroReady =
                    "true";

                  hero.dispatchEvent(
                    new CustomEvent(
                      "nbr:hero-ready",
                      {
                        bubbles: true,
                        detail: {
                          sectionId:
                            hero.dataset
                              .sectionId ||
                            ""
                        }
                      }
                    )
                  );
                }
              );
          }
        );
    }

    function handleIntersection(
      entries
    ) {
      entries.forEach(
        (entry) => {
          if (
            entry.target !== hero
          ) {
            return;
          }

          state.inView =
            entry.isIntersecting;

          hero.dataset.nbrHeroVisible =
            state.inView
              ? "true"
              : "false";

          if (state.inView) {
            updateScrollState();
            requestPointerRender();
          } else {
            resetPointerLighting();
          }

          updateVideoState();
        }
      );
    }

    function createVisibilityObserver() {
      if (
        !(
          "IntersectionObserver" in
          window
        )
      ) {
        state.inView = true;
        return;
      }

      intersectionObserver =
        new IntersectionObserver(
          handleIntersection,
          {
            threshold: [
              0,
              0.02,
              0.25
            ],
            rootMargin:
              "140px 0px"
          }
        );

      intersectionObserver.observe(
        hero
      );
    }

    function createResizeObserver() {
      if (
        !(
          "ResizeObserver" in
          window
        )
      ) {
        return;
      }

      resizeObserver =
        new ResizeObserver(
          () => {
            requestGlobalScrollUpdate();
          }
        );

      resizeObserver.observe(hero);
    }

    function handleSectionVisibility(
      event
    ) {
      const detail =
        event.detail || {};

      state.inView =
        detail.visible !== false;

      hero.dataset.nbrHeroVisible =
        state.inView
          ? "true"
          : "false";

      if (state.inView) {
        requestGlobalScrollUpdate();
        requestPointerRender();
      } else {
        resetPointerLighting();
      }

      updateVideoState();
    }

    function handleMotionChange() {
      if (
        !pointerLightingAllowed()
      ) {
        resetPointerLighting(true);
      } else {
        requestPointerRender();
      }

      updateVideoState();
    }

    function refresh() {
      if (state.destroyed) {
        return;
      }

      state.pageVisible =
        !document.hidden;

      handleMotionChange();
      updateScrollState();

      if (
        !hero.classList.contains(
          "nbr-hero--ready"
        )
      ) {
        revealHero();
      }
    }

    function destroy() {
      if (state.destroyed) {
        return;
      }

      state.destroyed = true;

      localAbortController.abort();

      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (
        state.pointerFrameId !== null
      ) {
        window.cancelAnimationFrame(
          state.pointerFrameId
        );
      }

      if (
        state.readyFrameOne !== null
      ) {
        window.cancelAnimationFrame(
          state.readyFrameOne
        );
      }

      if (
        state.readyFrameTwo !== null
      ) {
        window.cancelAnimationFrame(
          state.readyFrameTwo
        );
      }

      if (video) {
        video.pause();
      }

      hero.style.removeProperty(
        "--hero-light-x"
      );

      hero.style.removeProperty(
        "--hero-light-y"
      );

      hero.style.removeProperty(
        "--hero-pointer-x"
      );

      hero.style.removeProperty(
        "--hero-pointer-y"
      );

      hero.style.removeProperty(
        "--hero-viewport-progress"
      );

      hero.style.removeProperty(
        "--hero-scroll-progress"
      );

      hero.style.removeProperty(
        "--hero-scroll-offset"
      );

      hero.classList.remove(
        "nbr-hero--ready",
        "nbr-hero--video-ready",
        "nbr-hero--video-paused"
      );

      delete hero.dataset
        .nbrHeroReady;

      delete hero.dataset
        .nbrHeroVisible;

      delete hero[INSTANCE_KEY];

      instances.delete(instance);
    }

    listen(
      hero,
      "pointermove",
      handlePointerMove,
      { passive: true },
      localSignal
    );

    listen(
      hero,
      "pointerleave",
      () => {
        resetPointerLighting();
      },
      undefined,
      localSignal
    );

    listen(
      hero,
      "pointercancel",
      () => {
        resetPointerLighting();
      },
      undefined,
      localSignal
    );

    listen(
      hero,
      "nbr:visibility",
      handleSectionVisibility,
      undefined,
      localSignal
    );

    if (video) {
      listen(
        video,
        "playing",
        handleVideoPlaying,
        undefined,
        localSignal
      );

      listen(
        video,
        "pause",
        handleVideoPause,
        undefined,
        localSignal
      );

      listen(
        video,
        "loadeddata",
        () => {
          hero.classList.add(
            "nbr-hero--video-ready"
          );
        },
        { once: true },
        localSignal
      );
    }

    createVisibilityObserver();
    createResizeObserver();

    setLighting(0, 0);
    updateScrollState();
    updateVideoState();
    revealHero();

    const instance = {
      hero,
      refresh,
      destroy,
      updateScrollState,
      updateVideoState,
      handleMotionChange
    };

    hero[INSTANCE_KEY] = instance;
    instances.add(instance);

    return instance;
  }

  function initialiseHeroes(
    container = document
  ) {
    getHeroes(container).forEach(
      (hero) => {
        createHeroInstance(hero);
      }
    );
  }

  function refreshHeroes(
    container = document
  ) {
    initialiseHeroes(container);

    getHeroes(container).forEach(
      (hero) => {
        const instance =
          hero[INSTANCE_KEY];

        if (instance) {
          instance.refresh();
        }
      }
    );
  }

  function destroyHeroes(
    container = document
  ) {
    getHeroes(container).forEach(
      (hero) => {
        const instance =
          hero[INSTANCE_KEY];

        if (instance) {
          instance.destroy();
        }
      }
    );
  }

  function handleGlobalVisibility() {
    instances.forEach(
      (instance) => {
        instance.refresh();
      }
    );
  }

  function handleGlobalMotionChange() {
    instances.forEach(
      (instance) => {
        instance.handleMotionChange();
      }
    );
  }

  function bindGlobalEvents() {
    const signal =
      globalAbortController.signal;

    listen(
      window,
      "scroll",
      requestGlobalScrollUpdate,
      { passive: true },
      signal
    );

    listen(
      window,
      "resize",
      () => {
        requestGlobalScrollUpdate();
        handleGlobalMotionChange();
      },
      { passive: true },
      signal
    );

    listen(
      window,
      "orientationchange",
      () => {
        requestGlobalScrollUpdate();
        handleGlobalMotionChange();
      },
      { passive: true },
      signal
    );

    listen(
      window,
      "nbr:qualitychange",
      handleGlobalMotionChange,
      undefined,
      signal
    );

    listen(
      document,
      "visibilitychange",
      handleGlobalVisibility,
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:load",
      (event) => {
        refreshHeroes(event.target);
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:unload",
      (event) => {
        destroyHeroes(event.target);
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:reorder",
      () => {
        refreshHeroes(document);
        requestGlobalScrollUpdate();
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:section:select",
      (event) => {
        refreshHeroes(event.target);
      },
      undefined,
      signal
    );

    listen(
      document,
      "shopify:block:select",
      (event) => {
        refreshHeroes(event.target);
      },
      undefined,
      signal
    );

    listenToMediaQuery(
      mediaQueries.reducedMotion,
      handleGlobalMotionChange,
      signal
    );

    listenToMediaQuery(
      mediaQueries.coarsePointer,
      handleGlobalMotionChange,
      signal
    );

    listenToMediaQuery(
      mediaQueries.noHover,
      handleGlobalMotionChange,
      signal
    );

    listenToMediaQuery(
      mediaQueries.mobile,
      handleGlobalMotionChange,
      signal
    );
  }

  const controller = {
    version: CONTROLLER_VERSION,

    refresh(
      container = document
    ) {
      refreshHeroes(container);
      requestGlobalScrollUpdate();
    },

    destroy(
      container = document
    ) {
      destroyHeroes(container);
    },

    destroyAll() {
      globalAbortController.abort();

      Array.from(instances).forEach(
        (instance) => {
          instance.destroy();
        }
      );

      if (scrollFrameId !== null) {
        window.cancelAnimationFrame(
          scrollFrameId
        );

        scrollFrameId = null;
      }

      if (
        window[CONTROLLER_KEY] ===
        controller
      ) {
        delete window[CONTROLLER_KEY];
      }
    }
  };

  window[CONTROLLER_KEY] =
    controller;

  bindGlobalEvents();
  initialiseHeroes(document);
  requestGlobalScrollUpdate();
})();